import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { usePermissions } from './PermissionsContext'; 
import PermissionDeniedModal from './components/PermissionDeniedModal';
import Modal from './components/Modal';

function Settings() {
  const [taxRates, setTaxRates] = useState([]);
  const [lang, setLang] = useState('en');

  // Stripe API Key State
  const [stripeApiKey, setStripeApiKey] = useState('');
  const [apiKeyExists, setApiKeyExists] = useState(false);
  const [stripeEnv, setStripeEnv] = useState(null);
  const [apiKeyStatus, setApiKeyStatus] = useState('idle'); 

  // Modal alert for invalid API key
  const [isApiKeyErrorModalOpen, setIsApiKeyErrorModalOpen] = useState(false);
  const [apiKeyErrorMessage, setApiKeyErrorMessage] = useState('');

  // Tenant Settings (Invoice Memo/Footer)
  const [tenantSettings, setTenantSettings] = useState({
    invoiceDescription: '',
    invoiceFooter: ''
  });
  const [settingsStatus, setSettingsStatus] = useState('idle');

  // Auth state
  const [userToken, setUserToken] = useState(null);
  const [authInitialized, setAuthInitialized] = useState(false);
  
  const [isPermissionDeniedModalOpen, setPermissionDeniedModalOpen] = useState(false);
  
  const { permissions, isAdmin, loadingPermissions } = usePermissions();

  // Stripe Tax Rates Modal State
  const [isStripeTaxRatesModalOpen, setStripeTaxRatesModalOpen] = useState(false);
  const [stripeTaxRates, setStripeTaxRates] = useState([]);
  const [loadingStripeTaxRates, setLoadingStripeTaxRates] = useState(false);

  const translations = useMemo(() => ({
    ja: {
      mainTitle: "Billwave設定",
      subTitle: "税率設定",
      tableNo: "No.",
      tableTaxRateId: "Stripe税率ID (tax_rate_id)",
      actions: "操作",
      saveButton: "保存",
      deleteButton: "削除",
      addButton: "行を追加",
      saving: "保存中...",
      deleting: "削除中...",
      saved: "保存済み",
      error: "エラーが発生しました",
      invalidTaxRateId: "無効な税率IDです。",
      deleteConfirm: "この税率設定を完全に削除しますか？この操作は元に戻せません。",
      newRow: "新規",
      stripeSettingsTitle: "Stripe APIキー設定",
      apiKeyLabel: "Stripeシークレットキー",
      apiKeyPlaceholder: "rk_...",
      apiKeyStatusLabel: "ステータス",
      apiKeySet: "設定済み",
      apiKeyNotSet: "未設定",
      stripeEnvLabel: "Stripe環境",
      stripeEnvLive: "本番",
      stripeEnvTest: "テスト",
      saveApiKeyButton: "APIキーを保存",
      savingApiKey: "保存中...",
      apiKeySaved: "保存しました",
      apiKeyError: "APIキーの保存に失敗しました",
      apiKeyInvalidTitle: "Stripe APIキー エラー",
      apiKeyInvalidMessage: "入力されたStripe APIキーが無効または認証できませんでした。正しいキーを入力してください。",
      loading: "読み込み中...",
      authLoading: "認証情報を読み込んでいます...",
      authRequired: "設定にアクセスするにはログインしてください。",
      invoiceSettingsTitle: "請求書設定",
      invoiceDescriptionLabel: "請求書メモ（最大500文字）",
      invoiceDescriptionPlaceholder: "請求書上部に表示されるメモです。",
      invoiceDescriptionHint: "請求書上部に表示されるメモです。改行はそのまま反映されます（最大500文字）。",
      invoiceFooterLabel: "請求書フッター（最大500文字）",
      invoiceFooterPlaceholder: "請求書下部に表示されるメッセージです。",
      invoiceFooterHint: "請求書下部に表示されるメッセージです。改行はそのまま反映されます（最大500文字）。",
      saveSettingsButton: "請求書設定を保存",
      settingsSaved: "設定を保存しました",
      settingsError: "設定の保存に失敗しました",
      checkStripeTaxRates: "税率IDを確認する",
      stripeTaxRatesTitle: "Stripe登録済み税率一覧",
      stripeTaxRateId: "税率ID",
      stripeTaxRatePercentage: "税率",
      stripeTaxRateInclusive: "税込・税抜",
      stripeTaxRateStatus: "有効・無効",
      inclusive: "税込",
      exclusive: "税抜",
      active: "有効",
      inactive: "無効",
      closeButton: "OK",
    },
    en: {
      mainTitle: "BillWave Settings",
      subTitle: "Tax Rate Settings",
      tableNo: "No.",
      tableTaxRateId: "Stripe Tax Rate ID (tax_rate_id)",
      actions: "Actions",
      saveButton: "Save",
      deleteButton: "Delete",
      addButton: "Add Row",
      saving: "Saving...",
      deleting: "Deleting...",
      saved: "Saved",
      error: "An error occurred",
      invalidTaxRateId: "Invalid Tax Rate ID.",
      deleteConfirm: "Are you sure you want to permanently delete this tax rate? This action cannot be undone.",
      newRow: "New",
      stripeSettingsTitle: "Stripe API Key Settings",
      apiKeyLabel: "Stripe Secret Key",
      apiKeyPlaceholder: "rk_...",
      apiKeyStatusLabel: "Status",
      apiKeySet: "Configured",
      apiKeyNotSet: "Not Set",
      stripeEnvLabel: "Stripe Environment",
      stripeEnvLive: "Live",
      stripeEnvTest: "Test",
      saveApiKeyButton: "Save API Key",
      savingApiKey: "Saving...",
      apiKeySaved: "Saved",
      apiKeyError: "Failed to save API key",
      apiKeyInvalidTitle: "Stripe API Key Error",
      apiKeyInvalidMessage: "The entered Stripe API key is invalid or could not be authenticated. Please provide a valid key.",
      loading: "Loading...",
      authLoading: "Loading authentication...",
      authRequired: "Please log in to access settings.",
      invoiceSettingsTitle: "Invoice Settings",
      invoiceDescriptionLabel: "Invoice Description (Max 500 characters)",
      invoiceDescriptionPlaceholder: "Memo shown at the top of the invoice.",
      invoiceDescriptionHint: "This memo will appear at the top of the invoice. Line breaks are preserved (Max 500 characters).",
      invoiceFooterLabel: "Invoice Footer (Max 500 characters)",
      invoiceFooterPlaceholder: "Message shown at the bottom of the invoice.",
      invoiceFooterHint: "This message will appear at the bottom of the invoice. Line breaks are preserved (Max 500 characters).",
      saveSettingsButton: "Save Invoice Settings",
      settingsSaved: "Settings saved",
      settingsError: "Failed to save settings",
      checkStripeTaxRates: "Check Tax Rate IDs",
      stripeTaxRatesTitle: "Registered Stripe Tax Rates",
      stripeTaxRateId: "Tax Rate ID",
      stripeTaxRatePercentage: "Rate",
      stripeTaxRateInclusive: "Type",
      stripeTaxRateStatus: "Status",
      inclusive: "Inclusive",
      exclusive: "Exclusive",
      active: "Active",
      inactive: "Inactive",
      closeButton: "OK",
    },
  }), []);

  const t = translations[lang];

  useEffect(() => {
    const userLang = navigator.language.startsWith('ja') ? 'ja' : 'en';
    setLang(userLang);
    document.title = t.mainTitle + " - BillWave";
  }, [t.mainTitle, lang]);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const token = await user.getIdToken();
          setUserToken(token);
        } catch (error) {
          console.error("Error getting user token:", error);
          setUserToken(null);
        }
      } else {
        setUserToken(null);
      }
      setAuthInitialized(true);
    });
    return () => unsubscribe();
  }, []);

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    if (!userToken) {
      throw new Error('No user token available. Please log in.');
    }
    const newOptions = { ...options };
    newOptions.headers = { ...newOptions.headers, Authorization: `Bearer ${userToken}` };
    if (newOptions.body && typeof newOptions.body === 'object') {
      newOptions.headers['Content-Type'] = 'application/json';
      newOptions.body = JSON.stringify(newOptions.body);
    }
    const response = await fetch(url, newOptions);
    if (!response.ok) {
      const errorText = await response.text();
      let error;
      try {
        const errorJson = JSON.parse(errorText);
        error = new Error(errorJson.message || errorJson.error || `Request failed with status ${response.status}`);
        error.code = errorJson.code;
      } catch (e) {
        error = new Error(errorText || `Request failed with status ${response.status}`);
      }
      throw error;
    }
    return response;
  }, [userToken]);

  const checkApiKeyStatus = useCallback(async () => {
    if (!authInitialized || !userToken) return;
    setApiKeyStatus('loading');
    try {
      const response = await fetchWithAuth('/v1/stripe-api-key-status');
      const data = await response.json();
      setApiKeyExists(data.exists);
      setStripeEnv(data.environment || null);
      setApiKeyStatus('idle');
    } catch (error) {
      console.error('API key status error:', error.message);
      setApiKeyStatus('error');
    }
  }, [authInitialized, userToken, fetchWithAuth]);

  const fetchTaxRates = useCallback(async () => {
    if (!authInitialized || !userToken) return;
    try {
      const response = await fetchWithAuth('/v1/tax-rates');
      const data = await response.json();
      setTaxRates(data.map(rate => ({ ...rate, status: 'default', isNew: false })));
    } catch (error) {
      console.error('Fetch error:', error.message);
    }
  }, [authInitialized, userToken, fetchWithAuth]);

  const fetchTenantSettings = useCallback(async () => {
    if (!authInitialized || !userToken) return;
    try {
      const response = await fetchWithAuth('/v1/tenant-settings');
      const data = await response.json();
      setTenantSettings(data);
    } catch (error) {
      console.error('Fetch tenant settings error:', error.message);
    }
  }, [authInitialized, userToken, fetchWithAuth]);

  useEffect(() => {
    if (authInitialized) {
      fetchTaxRates();
      checkApiKeyStatus();
      fetchTenantSettings();
    }
  }, [authInitialized, fetchTaxRates, checkApiKeyStatus, fetchTenantSettings]);

  const handleSaveApiKey = async (e) => {
    e.preventDefault();
    if (!isAdmin && !permissions.settings?.canUpdateBillwave) {
      setPermissionDeniedModalOpen(true);
      return;
    }

    const trimmedKey = stripeApiKey.trim();
    if (!trimmedKey.startsWith('sk_') && !trimmedKey.startsWith('rk_')) {
      setApiKeyErrorMessage(t.apiKeyInvalidMessage);
      setIsApiKeyErrorModalOpen(true);
      return;
    }

    setApiKeyStatus('saving');
    try {
      const response = await fetchWithAuth('/v1/stripe-api-key', {
        method: 'POST',
        body: { 
          apiKey: trimmedKey,
        },
      });
      const data = await response.json();
      setApiKeyStatus('saved');
      setApiKeyExists(true);
      if (data.environment) {
        setStripeEnv(data.environment);
      } else {
        await checkApiKeyStatus();
      }
      setStripeApiKey('');
    } catch (error) {
      console.error('Save API key error:', error.message);
      setApiKeyStatus('error');
      setApiKeyErrorMessage(error.message || t.apiKeyInvalidMessage);
      setIsApiKeyErrorModalOpen(true);
    }
  };

  const handleSaveTenantSettings = async (e) => {
    e.preventDefault();
    if (!isAdmin && !permissions.settings?.canUpdateBillwave) {
      setPermissionDeniedModalOpen(true);
      return;
    }
    setSettingsStatus('saving');
    try {
      await fetchWithAuth('/v1/tenant-settings', {
        method: 'POST',
        body: tenantSettings,
      });
      setSettingsStatus('saved');
    } catch (error) {
      console.error('Save tenant settings error:', error.message);
      setSettingsStatus('error');
    }
  };

  const updateRateStatus = (id, status, errorMessage = '') => {
    setTaxRates(prevRates =>
      prevRates.map(r => (r.id === id ? { ...r, status, error: errorMessage } : r))
    );
  };

  const handleSave = async (id) => {
    if (!isAdmin && !permissions.settings?.canUpdateBillwave) {
      setPermissionDeniedModalOpen(true);
      return;
    }
    const rate = taxRates.find(r => r.id === id);
    if (!rate) return;

    updateRateStatus(id, 'saving');

    try {
      const payload = { id: rate.isNew ? null : rate.id, tax_rate_id: rate.tax_rate_id };
      await fetchWithAuth('/v1/tax-rates', {
        method: 'POST',
        body: payload,
      });
      fetchTaxRates();
    } catch (error) {
      console.error('Save error:', error.message);
      updateRateStatus(id, 'error', error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!isAdmin && !permissions.settings?.canUpdateBillwave) {
      setPermissionDeniedModalOpen(true);
      return;
    }
    if (!window.confirm(t.deleteConfirm)) return;

    const rate = taxRates.find(r => r.id === id);
    if (!rate || rate.isNew) {
      setTaxRates(prevRates => prevRates.filter(r => r.id !== id));
      return;
    }

    updateRateStatus(id, 'deleting');

    try {
      await fetchWithAuth(`/v1/tax-rates/${id}`, { method: 'DELETE' });
      await fetchTaxRates();
    } catch (error) {
      console.error('Delete error:', error.message);
      updateRateStatus(id, 'error', error.message);
    }
  };

  const handleAddRow = () => {
    if (!isAdmin && !permissions.settings?.canUpdateBillwave) {
      setPermissionDeniedModalOpen(true);
      return;
    }
    const tempId = Date.now();
    setTaxRates([...taxRates, { id: tempId, tax_rate_id: '', status: 'default', isNew: true }]);
  };

  const handleInputChange = (id, value) => {
    setTaxRates(prevRates =>
      prevRates.map(r => (r.id === id ? { ...r, tax_rate_id: value, status: 'default' } : r))
    );
  };

  const handleFetchStripeTaxRates = async () => {
    setLoadingStripeTaxRates(true);
    setStripeTaxRatesModalOpen(true);
    try {
      const response = await fetchWithAuth('/v1/tax-rates/stripe');
      const data = await response.json();
      setStripeTaxRates(data);
    } catch (error) {
      console.error('Fetch Stripe tax rates error:', error.message);
    } finally {
      setLoadingStripeTaxRates(false);
    }
  };

  if (!authInitialized || loadingPermissions) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-xl text-gray-500">{t.authLoading}</p>
      </div>
    );
  }

  if (!userToken) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md text-center">
              <h1 className="text-2xl font-bold text-red-600 mb-4">Authentication Required</h1>
              <p className="text-gray-700">{t.authRequired}</p>
          </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 w-full max-w-4xl mx-auto my-8 shadow-lg rounded-lg">
      <PermissionDeniedModal isOpen={isPermissionDeniedModalOpen} onClose={() => setPermissionDeniedModalOpen(false)} />
      
      {/* Invalid API Key Error Modal */}
      <Modal isOpen={isApiKeyErrorModalOpen} onClose={() => setIsApiKeyErrorModalOpen(false)} title={t.apiKeyInvalidTitle}>
        <div className="p-4">
          <div className="flex items-center text-red-600 mb-4">
            <svg className="w-8 h-8 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
            <p className="text-sm font-semibold text-gray-800">{apiKeyErrorMessage || t.apiKeyInvalidMessage}</p>
          </div>
          <div className="flex justify-end mt-6">
            <button
              onClick={() => setIsApiKeyErrorModalOpen(false)}
              className="py-2 px-6 rounded-lg bg-red-600 text-white font-bold hover:bg-red-700 transition-colors"
            >
              {t.closeButton}
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isStripeTaxRatesModalOpen} onClose={() => setStripeTaxRatesModalOpen(false)} title={t.stripeTaxRatesTitle}>
        {loadingStripeTaxRates ? (
          <div className="p-8 text-center">{t.loading}</div>
        ) : (
          <div>
            <div className="overflow-x-auto max-h-[60vh] mb-4">
              <table className="w-full border-collapse table-auto">
                <thead className="sticky top-0 bg-white shadow-sm">
                  <tr className="bg-gray-100">
                    <th className="border p-2 text-left text-xs font-semibold text-gray-600">{t.stripeTaxRateId}</th>
                    <th className="border p-2 text-left text-xs font-semibold text-gray-600">{t.stripeTaxRatePercentage}%</th>
                    <th className="border p-2 text-left text-xs font-semibold text-gray-600">{t.stripeTaxRateInclusive}</th>
                    <th className="border p-2 text-left text-xs font-semibold text-gray-600">{t.stripeTaxRateStatus}</th>
                  </tr>
                </thead>
                <tbody>
                  {stripeTaxRates.map((rate) => (
                    <tr key={rate.id} className="hover:bg-gray-50">
                      <td className="border p-2 text-xs font-mono select-all">{rate.id}</td>
                      <td className="border p-2 text-xs">{rate.percentage}%</td>
                      <td className="border p-2 text-xs">{rate.inclusive ? t.inclusive : t.exclusive}</td>
                      <td className="border p-2 text-xs">
                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${rate.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {rate.active ? t.active : t.inactive}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setStripeTaxRatesModalOpen(false)}
                className="py-2 px-6 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors"
              >
                {t.closeButton}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <h1 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-gray-200">{t.mainTitle}</h1>

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{t.stripeSettingsTitle}</h2>
        <div className="p-4 border rounded-lg bg-gray-50">
          <div className="mb-4 flex flex-wrap gap-6 items-center">
            <div>
              <span className="font-semibold">{t.apiKeyStatusLabel}: </span>
              {apiKeyStatus === 'loading' ? (
                <span className="text-gray-500">{t.loading}</span>
              ) : (
                <span className={apiKeyExists ? "text-green-600 font-bold" : "text-red-600 font-bold"}>
                  {apiKeyExists ? t.apiKeySet : t.apiKeyNotSet}
                </span>
              )}
            </div>
            {apiKeyExists && stripeEnv && (
              <div>
                <span className="font-semibold">{t.stripeEnvLabel}: </span>
                <span className={`font-bold px-2 py-0.5 rounded text-sm ${stripeEnv === 'live' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {stripeEnv === 'live' ? t.stripeEnvLive : t.stripeEnvTest}
                </span>
              </div>
            )}
          </div>
          <form onSubmit={handleSaveApiKey}>
            <label htmlFor="stripe-api-key" className="block text-sm font-medium text-gray-700 mb-1">{t.apiKeyLabel}</label>
            <div className="flex items-center space-x-2">
              <input
                id="stripe-api-key"
                type="password"
                value={stripeApiKey}
                onChange={(e) => setStripeApiKey(e.target.value)}
                placeholder={t.apiKeyPlaceholder}
                className="flex-grow p-2 border rounded-md focus:ring-2 focus:ring-blue-500 transition"
              />
              <button
                type="submit"
                disabled={apiKeyStatus === 'saving' || !stripeApiKey}
                className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {apiKeyStatus === 'saving' ? t.savingApiKey : t.saveApiKeyButton}
              </button>
            </div>
             {apiKeyStatus === 'saved' && <p className="text-green-600 text-sm mt-2 animate-pulse">{t.apiKeySaved}</p>}
             {apiKeyStatus === 'error' && <p className="text-red-600 text-sm mt-2">{t.apiKeyError}</p>}
          </form>
        </div>
      </div>

      {/* Invoice Custom Messages Settings */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{t.invoiceSettingsTitle}</h2>
        <div className="p-4 border rounded-lg bg-gray-50">
          <form onSubmit={handleSaveTenantSettings} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.invoiceDescriptionLabel}</label>
              <textarea
                value={tenantSettings.invoiceDescription}
                onChange={(e) => setTenantSettings({ ...tenantSettings, invoiceDescription: e.target.value })}
                placeholder={t.invoiceDescriptionPlaceholder}
                className="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 transition min-h-[100px]"
                maxLength={500}
              />
              <p className="mt-1 text-xs text-gray-500">{t.invoiceDescriptionHint}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t.invoiceFooterLabel}</label>
              <textarea
                value={tenantSettings.invoiceFooter}
                onChange={(e) => setTenantSettings({ ...tenantSettings, invoiceFooter: e.target.value })}
                placeholder={t.invoiceFooterPlaceholder}
                className="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 transition min-h-[100px]"
                maxLength={500}
              />
              <p className="mt-1 text-xs text-gray-500">{t.invoiceFooterHint}</p>
            </div>
            <button
              type="submit"
              disabled={settingsStatus === 'saving'}
              className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {settingsStatus === 'saving' ? t.saving : t.saveSettingsButton}
            </button>
            {settingsStatus === 'saved' && <p className="text-green-600 text-sm mt-2 animate-pulse">{t.settingsSaved}</p>}
            {settingsStatus === 'error' && <p className="text-red-600 text-sm mt-2">{t.settingsError}</p>}
          </form>
        </div>
      </div>

      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-800">{t.subTitle}</h2>
          <button
            onClick={handleFetchStripeTaxRates}
            className="text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-2 px-4 rounded-lg transition-colors"
          >
            {t.checkStripeTaxRates}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse table-auto">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-3 text-left text-sm font-semibold text-gray-600">{t.tableNo}</th>
                <th className="border p-3 text-left text-sm font-semibold text-gray-600">{t.tableTaxRateId}</th>
                <th className="border p-3 text-left text-sm font-semibold text-gray-600">{t.actions}</th>
              </tr>
            </thead>
            <tbody>
              {taxRates.map((rate) => (
                <tr key={rate.id} className="hover:bg-gray-50">
                  <td className="border p-3 w-16 text-center">{rate.isNew ? t.newRow : rate.id}</td>
                  <td className="border p-3">
                    <input
                      type="text"
                      value={rate.tax_rate_id}
                      placeholder="txr_..."
                      onChange={(e) => handleInputChange(rate.id, e.target.value)}
                      className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 transition"
                    />
                  </td>
                  <td className="border p-3 w-64 whitespace-nowrap">
                    <button
                      onClick={() => handleSave(rate.id)}
                      disabled={rate.status === 'saving' || rate.status === 'deleting'}
                      className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {rate.status === 'saving' ? t.saving : t.saveButton}
                    </button>
                    <button
                      onClick={() => handleDelete(rate.id)}
                      disabled={rate.status === 'saving'}
                      className="ml-2 bg-red-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-700 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {rate.status === 'deleting' ? t.deleting : t.deleteButton}
                    </button>
                    {rate.status === 'saved' && <span className="text-green-600 font-bold ml-4 animate-pulse">{t.saved}</span>}
                    {rate.status === 'error' && <span className="text-red-600 font-bold ml-4">{rate.error || t.error}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6">
            <button
                onClick={handleAddRow}
                className="bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700 transition-colors shadow-md"
            >
                {t.addButton}
            </button>
        </div>
      </div>
    </div>
  );
}

export default Settings;
